import { useNavigate } from "react-router-dom";
import { Aperture, Megaphone, WandSparkles, Palette, ArrowUpRight } from "lucide-react";
import { Eyebrow, Marker } from "./Bits";
import Reveal from "@/components/Reveal";
import useIsPhone from "@/hooks/useIsPhone";

/* Choose Your Learning Path — Figma node 102:3121 (Frame 128).

   Hover is rotation only, no scale-up: in the design the tilted card's top edge
   keeps its full width while dropping 28px across, which is a ~4deg rotation. */

/* `to` mirrors the Skill Bundles column of the Learning mega-menu in SiteHeader —
   these four cards are the same four bundles, so they must land on the same
   filtered pages. Keep the two lists in step if either set of targets moves. */
const PATHS = [
  {
    key: "creator",
    title: "Creator\nEconomy",
    icon: Aperture,
    bg: "#F7CFE3",
    ink: "#8E1071",
    to: "/courses?category=content",
    skills: ["Content Creation", "Personal Branding", "Video Editing", "YouTube Growth", "Instagram Growth"],
  },
  {
    key: "marketing",
    title: "Marketing &\nBusiness",
    icon: Megaphone,
    bg: "#A5E6C4",
    ink: "#0B5C2E",
    to: "/courses?category=marketing",
    skills: ["Digital Marketing", "Performance Marketing", "Affiliate Marketing", "Sales & Lead Generation", "Entrepreneurship"],
  },
  {
    key: "ai",
    title: "AI &\nTechnology",
    icon: WandSparkles,
    bg: "#FAF07F",
    ink: "#6B6410",
    to: "/courses?category=general",
    skills: ["AI Automation", "Prompt Engineering", "ChatGPT Mastery", "Web Development", "No-Code Tools"],
  },
  {
    key: "workplace",
    title: "Workplace\nSkills",
    icon: Palette,
    bg: "#A9EDE6",
    ink: "#12457F",
    to: "/courses?category=freelancing",
    skills: ["UI/UX Design", "Resume Building", "Interview Preparation", "Communication Skills", "Freelancing"],
  },
];

export default function LearningPathSection({ paths }) {
  const navigate = useNavigate();
  const isPhone = useIsPhone();

  // Admin-managed skill paths drive the cards when present, presets otherwise.
  // Only titles/skills come from the API — the colour pairing stays with the design.
  const cards = paths && paths.length
    ? paths.slice(0, 4).map((p, i) => ({
        ...PATHS[i % PATHS.length],
        key: p.id || PATHS[i % PATHS.length].key,
        id: p.id,
        title: p.title || PATHS[i % PATHS.length].title,
        skills: (p.skills?.length ? p.skills : (p.courses || []).map((c) => c.title)).slice(0, 5),
      }))
    : PATHS;

  return (
    // The card row is 1413 of a 1426 frame — effectively edge to edge — so padding
    // stays minimal at lg to keep that proportion.
    <section id="skill-paths" className="mx-auto max-w-[1900px] px-5 py-12 sm:px-6 lg:px-3 lg:py-8 xl:py-10">
      <Reveal className="flex flex-col items-center gap-6 text-center">
        <Eyebrow>Explore our learning ecosystem</Eyebrow>
        <h2 className="font-poppins text-[32px] font-medium leading-[1.65] text-black sm:text-[40px] lg:text-[48px] xl:text-[54px]">
          Choose Your<Marker>Learning</Marker>Path
        </h2>
        <p className="max-w-[1040px] font-poppins text-[16px] font-light leading-[1.55] text-black lg:text-[21px] xl:text-[25px]">
          Discover industry-led programs designed to help you build practical skills, create real-world impact, and unlock new opportunities.
        </p>
      </Reveal>

      {/* overflow-visible at lg so a hovered card can lift and tilt past the row
          bounds; the inline padding keeps an edge card clear of the viewport edge
          (body sets overflow-x:hidden, which would otherwise crop the outer two). */}
      {/* Below sm it is a swipe rail rather than a stack, so the four cards cost
          one screen instead of four; the rail bleeds to the viewport edge so the
          next card peeks. From sm up it is the original grid, untouched. */}
      {/* overflow-y-hidden is a guard, not cosmetic: with overflow-x auto CSS
          computes a visible overflow-y to auto, so anything overhanging the rail
          vertically turns it into a vertical scroller. */}
      <div className="hide-scrollbar -mx-5 ml-[0px] mt-12 flex snap-x snap-mandatory gap-4 overflow-x-auto overflow-y-hidden px-5 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-5 sm:overflow-visible sm:px-0 sm:pb-0 lg:mt-14 lg:grid-cols-4 lg:gap-7 lg:px-2 lg:py-5">
        {cards.map((p, i) => (
          <Reveal key={p.key} delay={i * 80} still={isPhone} className="w-[80vw] max-w-[320px] shrink-0 snap-start sm:w-auto sm:max-w-none sm:shrink">
            <div
              className="group flex h-full w-full flex-col rounded-[24px] p-6 pb-5 transition-[transform,box-shadow] duration-300 ease-out hover:-rotate-[4deg] hover:shadow-[0_18px_44px_0_rgba(0,0,0,0.18)] motion-reduce:hover:rotate-0 sm:p-7 sm:pb-6 lg:p-8 lg:pb-7 xl:p-[46px] xl:pb-[34px]"
              style={{ background: p.bg }}
            >
              <span className="flex h-[62px] w-[62px] shrink-0 items-center justify-center self-start rounded-full bg-white/55 lg:h-auto lg:w-auto lg:rounded-none lg:bg-transparent">
                <p.icon
                  className="h-8 w-8 shrink-0 sm:h-9 sm:w-9 lg:h-11 lg:w-11 xl:h-12 xl:w-12"
                  style={{ color: p.ink }}
                  strokeWidth={1.6}
                />
              </span>

              <h3
                className="mt-4 whitespace-pre-line font-montserrat text-[26px] font-semibold leading-[1.22] sm:mt-6 sm:text-[30px] lg:mt-7 xl:mt-9 xl:text-[40px]"
                style={{ color: p.ink }}
              >
                {p.title}
              </h3>

              {/* Below sm one bulleted skill per line; the chip wrap the stacked
                  layout needed is kept for sm–lg, where the cards are still a
                  two-up grid. */}
              <ul className="mb-6 mt-5 flex flex-col gap-2 sm:mb-7 sm:mt-7 sm:flex-row sm:flex-wrap lg:flex-col xl:mb-9 xl:mt-9">
                {p.skills.length === 0 ? (
                  <li className="font-poppins text-[15px] italic text-black/60">Courses coming soon</li>
                ) : (
                  p.skills.map((s) => (
                    <li
                      key={s}
                      className="flex gap-2 font-poppins text-[13.5px] leading-[1.35] text-black sm:rounded-full sm:bg-white/55 sm:px-3 sm:py-1.5 lg:rounded-none lg:bg-transparent lg:px-0 lg:py-0 lg:text-[15px] xl:text-[16px]"
                    >
                      <span className="inline sm:hidden lg:inline" aria-hidden>•</span>
                      {s}
                    </li>
                  ))
                )}
              </ul>

              <button
                onClick={() => navigate(p.id ? `/courses?path=${p.id}` : p.to)}
                className="mt-auto flex h-[52px] w-full items-center justify-between gap-3 rounded-[45px] bg-white pl-5 pr-1.5 font-poppins text-[16px] text-black transition-all hover:brightness-95 active:scale-95 lg:h-[48px] xl:h-[54px] xl:text-[17px]"
              >
                Join Now
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full xl:h-10 xl:w-10"
                  style={{ background: p.ink }}
                >
                  <ArrowUpRight size={16} className="text-white" />
                </span>
              </button>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
